
import React, { useState, useEffect } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { Article } from "@/types/article-types";
import ArticlesTable from "@/components/admin/articles/ArticlesTable";
import { PlusCircle, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";

interface ArticlesTabProps {
  articles: Article[];
  tags: any[];
  authors: any[];
  createArticle: any;
  updateArticle: any;
  deleteArticle: (id: string) => Promise<boolean>;
  createTag: any;
  uploadMedia: any;
}

const ArticlesTab = ({
  articles,
  tags,
  authors,
  deleteArticle
}: ArticlesTabProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Log when authors change
  useEffect(() => {
    console.log("ArticlesTab - Authors updated:", authors);
  }, [authors]);

  const handleAddNew = () => {
    if (authors.length === 0) {
      toast({
        variant: "destructive",
        title: "No authors available",
        description: "You need to create at least one author before creating articles."
      });
      return;
    }

    navigate("/admin/articles/new");
  };

  const handleEdit = (article: Article) => {
    navigate(`/admin/articles/${article.id}`);
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await deleteArticle(id);
      if (result) {
        toast({
          title: "Article deleted",
          description: "The article has been successfully deleted."
        });
      }
      return result;
    } catch (error: any) {
      console.error("Error deleting article:", error);
      toast({
        variant: "destructive",
        title: "Error deleting article",
        description: error.message || "Failed to delete article"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isArticlesLoading = articles === undefined;

  return (
    <TabsContent value="articles" className="space-y-4">
      <div className="flex justify-between items-center bg-corporate-primary-25 p-4 rounded-lg border border-corporate-primary-50">
        <h2 className="text-2xl font-bold text-corporate-primary-100">Articles Management</h2>
        <Button 
          onClick={handleAddNew} 
          className="flex items-center gap-2 bg-corporate-primary-75 hover:bg-corporate-primary-100"
          disabled={isLoading || authors.length === 0}
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
          Add New Article
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {authors.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-700">
          You need to create at least one author before you can create articles.
          Please go to the Authors tab and create an author first.
        </div>
      )}
      
      {isArticlesLoading ? (
        <div className="text-center p-8 border rounded-lg flex flex-col items-center gap-4 bg-gray-50">
          <Loader2 className="h-8 w-8 animate-spin text-corporate-primary-100" />
          <p className="text-corporate-primary-75">Loading articles...</p>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center p-8 border rounded-lg bg-gray-50">
          <p className="text-corporate-primary-75 mb-4">No articles found</p>
          <Button onClick={handleAddNew} variant="outline" disabled={authors.length === 0}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Create your first article
          </Button>
        </div>
      ) : (
        <ArticlesTable 
          articles={articles}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </TabsContent>
  );
};

export default ArticlesTab;
